import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const COMPANY = "ANA INTERNATIONAL PRIVATE LIMITED";
const EMAIL = "info@anainternationalpvt.com";
const ADDRESS = "S. No. 265/3A, Room No. 8, Jadhav Mala, Ahmednagar, Ahmed Nagar – 414001, Maharashtra, India";

const LegalLayout = ({ eyebrow, title, updated, children, testId }) => (
    <div data-testid={testId}>
        <section className="relative pt-40 pb-14 overflow-hidden">
            <div className="absolute inset-0 grid-bg" />
            <div className="max-w-4xl mx-auto px-6 lg:px-10 relative z-10">
                <Reveal>
                    <Eyebrow>{eyebrow}</Eyebrow>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl mt-6 leading-tight">{title}</h1>
                    <p className="text-xs text-[#7c7b75] font-mono tracking-widest mt-6 uppercase">Last updated: {updated}</p>
                </Reveal>
            </div>
        </section>
        <section className="pb-28">
            <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-10">{children}</div>
        </section>
    </div>
);

const Block = ({ title, children }) => (
    <div>
        <h2 className="font-display font-semibold text-xl text-[#f5f4f0] mb-4">{title}</h2>
        <div className="text-sm text-[#a3a29b] leading-relaxed space-y-3">{children}</div>
    </div>
);

export const PrivacyPolicy = () => (
    <>
        <SEO title="Privacy Policy | ANA International Private Limited" description="Privacy Policy of ANA International Private Limited — how we collect, use and protect your information across our website and digital platforms." path="/privacy-policy" />
        <LegalLayout eyebrow="Legal" title="Privacy Policy" updated="January 2026" testId="privacy-policy-page">
            <Block title="1. Introduction">
                <p>{COMPANY} ("ANA International", "we", "us") respects your privacy. This Privacy Policy explains how we collect, use and safeguard information when you visit anainternationalpvt.com or interact with our products, including Lyanka AI.</p>
            </Block>
            <Block title="2. Information We Collect">
                <p>Information you provide directly, such as your name, email address, company name and message content when you submit our contact form. We may also collect basic technical information such as browser type, device information and pages visited, used to improve the website experience.</p>
            </Block>
            <Block title="3. How We Use Information">
                <p>We use collected information to respond to enquiries, operate and improve our website and products, communicate about our services where appropriate, and meet legal or regulatory obligations.</p>
            </Block>
            <Block title="4. Data Sharing">
                <p>We do not sell your personal information. Information may be shared with trusted service providers who support our operations, subject to appropriate safeguards, or where required by law.</p>
            </Block>
            <Block title="5. Data Security">
                <p>We apply reasonable technical and organisational measures to protect information against unauthorised access, loss or misuse. No method of transmission over the internet is completely secure, and we cannot guarantee absolute security.</p>
            </Block>
            <Block title="6. Data Retention">
                <p>We retain personal information only for as long as necessary to fulfil the purposes described in this policy or as required by applicable law.</p>
            </Block>
            <Block title="7. Your Rights">
                <p>You may request access to, correction of, or deletion of your personal information by contacting us at {EMAIL}.</p>
            </Block>
            <Block title="8. Contact">
                <p>{COMPANY}<br />{ADDRESS}<br />Email: {EMAIL} · Phone: +91 87998 43634</p>
            </Block>
        </LegalLayout>
    </>
);

export const TermsOfService = () => (
    <>
        <SEO title="Terms of Service | ANA International Private Limited" description="Terms of Service governing the use of the ANA International Private Limited website and its digital products, including Lyanka AI." path="/terms-of-service" />
        <LegalLayout eyebrow="Legal" title="Terms of Service" updated="January 2026" testId="terms-of-service-page">
            <Block title="1. Acceptance of Terms">
                <p>By accessing anainternationalpvt.com or any product operated by {COMPANY}, you agree to these Terms of Service. If you do not agree, please do not use the website or our products.</p>
            </Block>
            <Block title="2. About Our Services">
                <p>{COMPANY} develops proprietary digital applications and web platforms, including Lyanka AI, an AI-powered creative platform for generating images and videos. Access to premium product capabilities is provided through subscription plans, which may be subject to separate product-specific terms.</p>
            </Block>
            <Block title="3. Acceptable Use">
                <p>You agree not to misuse the website or our products, including attempting unauthorised access, interfering with service operation, or using generated content in violation of applicable laws or third-party rights.</p>
            </Block>
            <Block title="4. Intellectual Property">
                <p>All content, branding, logos and software on this website are the property of {COMPANY} or its licensors and are protected by applicable intellectual property laws. Use of AI-generated content through our products is subject to the applicable platform terms and any model or content restrictions.</p>
            </Block>
            <Block title="5. Subscriptions and Payments">
                <p>Subscription plans, allocated usage and billing terms for products such as Lyanka AI will be published with the relevant product. Subscriptions renew as described at the time of purchase unless cancelled.</p>
            </Block>
            <Block title="6. Disclaimers">
                <p>The website and our products are provided on an "as is" and "as available" basis. We do not warrant uninterrupted or error-free operation, and we make no guarantees regarding specific outcomes from the use of our products.</p>
            </Block>
            <Block title="7. Limitation of Liability">
                <p>To the maximum extent permitted by law, {COMPANY} shall not be liable for indirect, incidental or consequential damages arising from the use of the website or our products.</p>
            </Block>
            <Block title="8. Governing Law">
                <p>These terms are governed by the laws of India. Courts at Ahmednagar, Maharashtra shall have jurisdiction over disputes arising from these terms.</p>
            </Block>
            <Block title="9. Contact">
                <p>{COMPANY}<br />{ADDRESS}<br />Email: {EMAIL} · Phone: +91 87998 43634</p>
            </Block>
        </LegalLayout>
    </>
);

export const RefundPolicy = () => (
    <>
        <SEO title="Refund Policy | ANA International Private Limited" description="Refund Policy of ANA International Private Limited for subscription-based digital products, including Lyanka AI." path="/refund-policy" />
        <LegalLayout eyebrow="Legal" title="Refund Policy" updated="January 2026" testId="refund-policy-page">
            <Block title="1. Scope">
                <p>This Refund Policy applies to paid subscriptions for digital products operated by {COMPANY}, including Lyanka AI.</p>
            </Block>
            <Block title="2. Subscription Fees">
                <p>Subscription fees are generally non-refundable once a billing period has begun, except where a refund is required under applicable law. You may cancel a subscription to prevent future renewals; access typically remains active until the end of the paid period.</p>
            </Block>
            <Block title="3. Exceptional Cases">
                <p>If you believe you have been charged in error, or experienced a technical issue that prevented access to paid capabilities, contact us at {EMAIL} with your account and payment details. Requests are reviewed on a case-by-case basis.</p>
            </Block>
            <Block title="4. Processing">
                <p>Approved refunds are processed to the original payment method. Processing time may vary depending on the payment provider.</p>
            </Block>
            <Block title="5. Contact">
                <p>{COMPANY}<br />{ADDRESS}<br />Email: {EMAIL} · Phone: +91 87998 43634</p>
            </Block>
        </LegalLayout>
    </>
);

export const CookiePolicy = () => (
    <>
        <SEO title="Cookie Policy | ANA International Private Limited" description="Cookie Policy of ANA International Private Limited — how cookies and similar technologies are used on our website." path="/cookie-policy" />
        <LegalLayout eyebrow="Legal" title="Cookie Policy" updated="January 2026" testId="cookie-policy-page">
            <Block title="1. What Are Cookies">
                <p>Cookies are small text files stored on your device when you visit a website. They help websites function and provide information to site operators.</p>
            </Block>
            <Block title="2. How We Use Cookies">
                <p>We use a limited set of cookies and similar technologies: essential cookies required for the website to function, and analytics-related technologies that help us understand how the website is used so we can improve it.</p>
            </Block>
            <Block title="3. Managing Cookies">
                <p>You can control or delete cookies through your browser settings. Disabling certain cookies may affect website functionality.</p>
            </Block>
            <Block title="4. Changes">
                <p>We may update this Cookie Policy from time to time. Updates will be posted on this page with a revised date.</p>
            </Block>
            <Block title="5. Contact">
                <p>{COMPANY}<br />{ADDRESS}<br />Email: {EMAIL} · Phone: +91 87998 43634</p>
            </Block>
        </LegalLayout>
    </>
);
